import React from 'react';
import { useDialog } from '../context/Dialog';
import ProductRestockDialog from '../components/Dashboard/catalog/RestockProductDialog/ProductRestockDialog';
import ActionConfirmationDialog from '../components/ui/ActionConfirmationDialog';
import { ProductInformation, ProductSaveAS } from '../types/Products/product';
import { updateProduct } from '../services/Products/updateProduct';
import { useSelector } from 'react-redux';
import { RootState } from '../store/store';
import useIonicToast from './useIonicToast';
import { useQueryClient } from '@tanstack/react-query';
import EditProductDialog from '../components/Dashboard/catalog/EditProductDialog/EditProductDialog';
import { deleteProduct } from '../services/Products/deletProduct';

type Props = {
  id?: number;
  productName?: string;
};

export default function useProductCardActions(props: Props) {
  const { openDialog, closeDialog, setBusy } = useDialog();

  const { activeStore } = useSelector((state: RootState) => state.userStores);
  const storeId = activeStore?.id;

  const { signedAccessToken: authToken } = useSelector(
    (state: RootState) => state.userSession
  );

  const { presentIonToast } = useIonicToast();
  const queryClient = useQueryClient();
  const handleCatalogInvalidation = async () => {
    await queryClient.invalidateQueries({
      queryKey: ['products-catalog'],
      exact: false,
    });
    await queryClient.invalidateQueries({
      queryKey: ['products-metrics'],
      exact: false,
    });
    await queryClient.invalidateQueries({
      queryKey: ['products-inventory'],
      exact: false,
    });
  };

  const handleUnlisting = async (
    currentStatus?: ProductSaveAS,
    callback?: (newValue?: ProductSaveAS) => void
  ) => {
    if (!authToken || !storeId || !props.id) return;
    try {
      setBusy(true);
      const new_save_as =
        currentStatus == 'published' ? 'archived' : 'published';

      await updateProduct(
        props.id,
        { save_as: new_save_as },
        authToken,
        storeId
      );

      await handleCatalogInvalidation();

      if (callback && typeof callback === 'function') {
        callback(new_save_as);
      }

      presentIonToast(
        `Product has successfully been ${currentStatus == 'published' ? 'Archived' : 'Published'}`
      );
    } catch (error: any) {
      presentIonToast(error.message, 'error');
    } finally {
      setBusy(false);
    }
  };

  const handleDeleting = async (callback?: () => void) => {
    if (!authToken || !storeId || !props.id) return;
    try {
      setBusy(true);
      await deleteProduct(props.id, authToken);
      await handleCatalogInvalidation();

      if (callback && typeof callback === 'function') {
        callback();
      }

      presentIonToast(`Product has successfully been deleted.`);
    } catch (error: any) {
      presentIonToast(error.message, 'error');
    } finally {
      setBusy(false);
    }
  };

  const onRestock = () => {
    if (!props.id) return;
    openDialog(
      <ProductRestockDialog
        id={props.id}
        productName={props.productName || ''}
      />
    );
  };

  const onRemove = (callback?: () => void) => {
    openDialog(
      <ActionConfirmationDialog
        onCancel={() => closeDialog({ keepParent: true })}
        onConfirm={async () => {
          await handleDeleting(callback);
          closeDialog({ keepParent: false });
        }}
        cancelText="Cancel"
        confirmText="Remove"
        message="Are you sure you want to Remove this product"
        title="Remove Product Confirmation"
      />
    );
  };

  const onUnlist = (
    currentStatus?: ProductSaveAS,
    callback?: (newValue?: ProductSaveAS) => void
  ) => {
    openDialog(
      <ActionConfirmationDialog
        onCancel={() => closeDialog({ keepParent: true })}
        onConfirm={async () => {
          await handleUnlisting(currentStatus, callback);
          closeDialog({ keepParent: true });
        }}
        cancelText="Cancel"
        confirmationType={
          currentStatus == 'published' ? 'destructive' : 'default'
        }
        confirmText={currentStatus == 'published' ? 'Unlist' : 'List'}
        message={`Are you sure you want to ${currentStatus == 'published' ? 'Unlist' : 'List'} this product`}
        title={`${currentStatus == 'published' ? 'Unlist' : 'List'} Product Confirmation`}
      />
    );
  };

  const onEdit = (product: Partial<ProductInformation>) =>
    openDialog(<EditProductDialog initialFormState={product} />, {
      allowBackgroundDismiss: false,
    });

  return { onEdit, onUnlist, onRemove, onRestock };
}
